import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Country, State } from '../models/woocommerce.model';
import { environment } from '../../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class WoocommerceService {
    public apiBaseUrl = `${environment.api.baseUrl}wc/v3`;
    private wooCommerceAuthHeader: string;

    constructor(private http: HttpClient) {
        // Generate the Basic Auth header value from environment variables
        const apiKey = environment.api.payment_username;
        const apiSecret = environment.api.payment_password;
        this.wooCommerceAuthHeader = 'Basic ' + btoa(`${apiKey}:${apiSecret}`);
    }

    private getHeaders(): HttpHeaders {
        return new HttpHeaders()
            .set('Authorization', this.wooCommerceAuthHeader)
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');
    }

    /**
     * Get list of countries from WooCommerce API
     * @returns Observable of Country array
     */
    getCountries(): Observable<Country[]> {
        const headers = this.getHeaders();
        console.log('Making request to:', `${this.apiBaseUrl}/data/countries`);
        return this.http.get<Country[]>(`${this.apiBaseUrl}/data/countries`, { headers });
    }

    getCountryDetails(countryCode: string): Observable<Country> {
        const headers = this.getHeaders();
        return this.http.get<Country>(`${this.apiBaseUrl}/data/countries/${countryCode}`, { headers });
    }

    /**
     * Get list of states for a specific country from WooCommerce API
     * @param countryCode The code of the country (e.g., 'US', 'IN')
     * @returns Observable of State array
     */
    getStatesForCountry(countryCode: string): Observable<State[]> {
        const headers = this.getHeaders();
        // Note: The endpoint /data/countries/${countryCode}/states is a common pattern.
        // Please verify this is the correct endpoint for your WooCommerce API version and setup.
        // If not, you may need to adjust it, or fetch all countries and then extract states client-side if necessary.
        return this.http.get<State[]>(`${this.apiBaseUrl}/data/countries/${countryCode}/states`, { headers });
    }

    /**
     * Update customer billing information
     * @param customerId The ID of the customer to update
     * @param billingData The billing information to update
     * @returns Observable of the updated customer data
     */
    updateCustomerBilling(customerId: number, billingData: any): Observable<any> {
        const headers = this.getHeaders();
        return this.http.put(`${this.apiBaseUrl}/customers/${customerId}`, billingData, { headers });
    }

    /**
     * Get customer details by ID from WooCommerce API
     * @param customerId The ID of the customer to fetch
     * @returns Observable of the customer data
     */
    getCustomerDetails(customerId: number): Observable<any> {
        const headers = this.getHeaders();
        return this.http.get(`${this.apiBaseUrl}/customers/${customerId}`, { headers });
    }
} 